import React, { useEffect } from "react";
import { useForm, Controller, Resolver } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import AppInput from "@src/components/AppInput";
import TimesIcon from "@assets/img/times-icon.svg";
import RightArrow from "@assets/img/right-arrow.svg";
import { theme } from "@src/theme";
import styled from "styled-components";
import Spinner from "@src/components/Spinner";
import { SubmitButton } from "@src/components/SubmitButton";
import { CloseButton } from "@src/components/CloseButton";
import { CreateGuestCurator } from "@src/types/curator";
import AppSelectDropdown from "@src/components/AppSelectDropdown";
import { useMyShows } from "@src/hooks/useMyShows";
import { useAuthCurator, useRole } from "@src/hooks/useAuth";
import { UnderlineTextButton } from "@src/components/UnderlineTextButton";
import Tooltip from "@src/components/Tooltip";

const ActionsButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  min-height: 50px;
`;
const PillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.margins.small};
  min-height: 0px;
  max-height: 100px;
  overflow-y: auto;
`;
const ShowPill = styled.div`
  padding: ${theme.margins.small};
  background-color: ${theme.colors.yellow};
  border-radius: 5px;
  color: ${theme.colors.darkerBg};
  font-weight: bold;
  width: 100%;
`;
const schema = yup
  .object({
    name: yup.string().required().max(255),
    email: yup.string().required().max(255),
    shows: yup.array().of(yup.string().required()).required(),
    showsDropDown: yup.string().optional().max(255),
    about: yup.string().optional().max(255),
    notes: yup.string().optional().nullable().max(250),
  })
  .required();
const editSchema = yup
  .object({
    name: yup.string().required().min(1).max(255),
    email: yup.string().optional().max(255),
    shows: yup.array().of(yup.string().required()).required(),
    showsDropDown: yup.string().optional().max(255),
    about: yup.string().optional().nullable().max(255),
    notes: yup.string().optional().nullable().max(250),
  })
  .required();

type CreateGuestCuratorFormProps = {
  initialValues?: CreateGuestCurator;
  editMode?: boolean;
  closeButtonHandler: () => void;
  submitButtonHandler: (data: CreateGuestCurator) => Promise<void>;
  fetchingCurators: boolean;
  isOpen: boolean;
  disableInputs?: boolean;
};

const defaultValues: CreateGuestCurator = {
  name: "",
  email: "",
  shows: [],
  showsDropDown: "",
  about: "",
  notes: "",
};

const GuestCuratorForm: React.FC<CreateGuestCuratorFormProps> = ({
  disableInputs,
  isOpen,
  initialValues,
  editMode,
  fetchingCurators,
  closeButtonHandler,
  submitButtonHandler,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
    setValue,
  } = useForm<CreateGuestCurator>({
    resolver: yupResolver(
      editMode ? editSchema : schema
    ) as Resolver<CreateGuestCurator>,
    defaultValues,
  });
  const curator = useAuthCurator();
  const roleType = useRole();
  const { myShows } = useMyShows({
    curatorId: curator!.id,
    roleType,
    searchText: "",
  });
  const [selectedShows, setSelectedShows] = React.useState<string[]>([]);

  // Auto-select single show if only one is available (create mode only)
  useEffect(() => {
    if (
      myShows.length === 1 &&
      !editMode &&
      isOpen &&
      selectedShows.length === 0 &&
      myShows[0]?.documentId
    ) {
      const singleShowId = myShows[0].documentId;
      setSelectedShows([singleShowId]);
      setValue("shows", [singleShowId], { shouldValidate: true });
    }
  }, [myShows, isOpen, editMode, selectedShows.length, setValue]);

  // Sync form field when selectedShows changes (especially important for edit mode)
  useEffect(() => {
    setValue("shows", selectedShows, { shouldValidate: true });
  }, [selectedShows, setValue]);

  useEffect(() => {
    if (editMode && initialValues) {
      const showsToSet = initialValues.shows || [];
      setSelectedShows(showsToSet);
      reset({
        ...initialValues,
        shows: showsToSet,
        showsDropDown: "", // Keep dropdown empty in edit mode
      });
    } else {
      if (!isOpen) resetFromAndStates();
    }
    return () => {
      reset(defaultValues);
    };
  }, [editMode, initialValues, isOpen, reset]);

  const onSubmit = async (data: CreateGuestCurator): Promise<void> => {
    if (fetchingCurators) return;
    await submitButtonHandler(data as CreateGuestCurator);
  };

  const resetFromAndStates = (): void => {
    reset(defaultValues);
    setSelectedShows([]);
  };
  const onSelectShow = (value: string): void => {
    setSelectedShows((prevState) => {
      const newValue = [...prevState, value];
      // Update the form field with the new shows array
      setValue("shows", newValue, { shouldValidate: true });
      // Clear the dropdown field after selection
      setValue("showsDropDown", "");
      return newValue;
    });
  };

  useEffect(() => {
    return (): void => {
      setSelectedShows([]);
      resetFromAndStates();
    };
  }, []);

  const onPillClick = (showId: string): void => {
    setSelectedShows((prevState) => {
      const newValue = prevState.filter((id) => id !== showId);
      // Update the form field with the updated shows array
      setValue("shows", newValue, { shouldValidate: true });
      return newValue;
    });
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ActionsButtonGroup>
        <CloseButton
          disabled={disableInputs}
          type="button"
          onClick={async () => {
            if (disableInputs) return;
            closeButtonHandler();
            resetFromAndStates();
          }}
        >
          <img
            src={TimesIcon}
            alt="X"
            style={{ width: "10px", height: "10px" }}
          />
        </CloseButton>
        {fetchingCurators && <Spinner small />}
        <SubmitButton
          disabled={fetchingCurators || disableInputs}
          type="submit"
        >
          <img
            src={RightArrow}
            alt="Submit"
            style={{ height: "50%", objectFit: "contain" }}
          />
        </SubmitButton>
      </ActionsButtonGroup>
      <div style={{ marginBottom: theme.margins.medium }}>
        <Controller
          disabled={disableInputs || fetchingCurators}
          name="name"
          control={control}
          render={({ field }) => (
            <AppInput
              label={"Name"}
              control={control}
              errors={errors}
              {...field}
              placeholder="Enter name"
            />
          )}
        />
      </div>

      <div style={{ marginBottom: theme.margins.medium }}>
        <Controller
          name="email"
          disabled={editMode}
          control={control}
          render={({ field }) => (
            <AppInput
              label={"Email"}
              control={control}
              errors={errors}
              {...field}
              placeholder="Enter email"
            />
          )}
        />
      </div>

      <div>
        <AppSelectDropdown
          selectContainerStyle={{
            marginBottom: theme.margins.small,
            minHeight: 0,
          }}
          disabled={disableInputs || fetchingCurators}
          name="showsDropDown"
          control={control}
          options={myShows.filter(
            (show) => !selectedShows.includes(show.documentId)
          )}
          labelAccessor="title"
          valueAccessor="documentId"
          label={"Shows"}
          errors={errors}
          onChange={onSelectShow}
          placeholder="Select shows"
        />
        <PillsContainer>
          {selectedShows.map((showId) => {
            const show = myShows.find((s) => s.documentId === showId);
            return (
              <ShowPill
                onClick={() => {
                  if (disableInputs || fetchingCurators) return;
                  onPillClick(showId);
                }}
                key={showId}
              >
                {show?.title}
              </ShowPill>
            );
          })}
        </PillsContainer>
      </div>

      <div style={{ marginTop: 80, marginBottom: theme.margins.medium }}>
        <Controller
          disabled={disableInputs || fetchingCurators}
          name="about"
          control={control}
          render={({ field }) => (
            <AppInput
              label={"About"}
              control={control}
              errors={errors}
              {...field}
              placeholder="Enter about"
            />
          )}
        />
      </div>
      {editMode && (
        <div style={{ marginBottom: theme.margins.medium }}>
          <Controller
            disabled={disableInputs || fetchingCurators}
            name="notes"
            control={control}
            render={({ field }) => (
              <AppInput
                label={"Notes (max 250 characters)"}
                control={control}
                errors={errors}
                {...field}
                placeholder="Admin notes (not visible to curator)"
              />
            )}
          />
        </div>
      )}
      {!editMode && (
        <div style={{ width: "45%", maxHeight: 100 }}>
          <Tooltip
            content={
              isValid ? "Ready to submit" : "Please fill in all required fields"
            }
          >
            <UnderlineTextButton disabled={disableInputs} type="submit">
              <span style={{ color: theme.colors.yellow }}>Send invite</span>
            </UnderlineTextButton>
          </Tooltip>
        </div>
      )}
    </form>
  );
};

export default GuestCuratorForm;
